import React, { useState, useEffect } from 'react';
import { Users, Building2, Linkedin, Twitter, Instagram, Youtube, Facebook } from 'lucide-react';
import { useLogo } from '../hooks/useLogo';
import { useAuthDialog } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SOCIAL_ICONS = {
  linkedin:  <Linkedin  className="h-4 w-4" />,
  twitter:   <Twitter   className="h-4 w-4" />,
  instagram: <Instagram className="h-4 w-4" />,
  youtube:   <Youtube   className="h-4 w-4" />,
  facebook:  <Facebook  className="h-4 w-4" />,
};

export default function Footer() {
  const logoUrl = useLogo();
  const { openLabAuth } = useAuthDialog();
  const [footerData, setFooterData] = useState({ social: {}, footer: {}, branding: {} });

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/footer-settings`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setFooterData(d); })
      .catch(() => {});
  }, []);

  const { social = {}, footer = {} } = footerData;
  const description    = footer.description    || 'Connecting researchers with verified life science distributors worldwide.';
  const copyrightName  = footer.copyright_name || 'BioMall';
  const termsUrl       = footer.terms_url       || '/terms';
  const disclaimerUrl  = footer.disclaimer_url  || '/disclaimer';
  const disclaimerLabel = footer.disclaimer_label || 'Disclaimer';

  const socialLinks = Object.entries(social).filter(([, url]) => url && url.trim());

  return (
    <footer className="bg-slate-900 text-white mt-12">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">

          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              {logoUrl
                ? <img src={logoUrl} alt="BioMall" className="h-7 w-auto object-contain brightness-0 invert" />
                : <span className="font-bold text-white">BioMall.ai</span>
              }
            </div>
            <p className="text-sm text-slate-400">{description}</p>

            {/* Social icons */}
            {socialLinks.length > 0 && (
              <div className="flex gap-3 mt-4">
                {socialLinks.map(([key, url]) => (
                  <a key={key} href={url} target="_blank" rel="noopener noreferrer"
                    className="text-slate-400 hover:text-white transition-colors">
                    {SOCIAL_ICONS[key]}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* For Distributors */}
          <div>
            <h4 className="font-semibold mb-4 flex items-center gap-2">
              <Users className="h-4 w-4" />
              For Distributors
            </h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><a href="/for-distributors" className="hover:text-white transition-colors">Partner with Us</a></li>
            </ul>
          </div>

          {/* For Manufacturers */}
          <div>
            <h4 className="font-semibold mb-4 flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              For Manufacturers
            </h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><a href="/for-manufacturers" className="hover:text-white transition-colors">List and Promote your Products</a></li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><a href="/aboutus"               className="hover:text-white transition-colors">About Us</a></li>
              <li><a href="/contact"                className="hover:text-white transition-colors">Contact Us</a></li>
              <li><a href="/careers"               className="hover:text-white transition-colors">Careers</a></li>
              <li><a href="/blog"                   className="hover:text-white transition-colors">Blog</a></li>
              <li><a href="/faq"                    className="hover:text-white transition-colors">FAQ</a></li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-slate-800 mt-8 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-400">
            &copy; {new Date().getFullYear()} {copyrightName}. All rights reserved.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-400">
            <a href={termsUrl}           className="hover:text-white transition-colors">Terms &amp; Conditions</a>
            <a href="/terms-of-sales"    className="hover:text-white transition-colors">Terms of Sale</a>
            <a href="/return-policy"     className="hover:text-white transition-colors">Return Policy</a>
            <a href="/privacy"           className="hover:text-white transition-colors">Privacy Policy</a>
            <a href={disclaimerUrl}      className="hover:text-white transition-colors">{disclaimerLabel}</a>
            <a href="/careers"           className="hover:text-white transition-colors">Careers</a>
            <a href="/for-distributors"  className="hover:text-white transition-colors">For Distributors</a>
            <a href="/for-manufacturers" className="hover:text-white transition-colors">For Manufacturers</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
